/* eslint-disable max-lines */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';
import { Layout, LinkButton, Rating, Typography } from 'fronton-react';
import { cn } from 'utils/classnames';
import { getPathname } from 'common/selectors/location';
import { Carousel } from 'common/components/carousel/carousel';
import { AtMobile } from 'common/components/adaptive/mobile';
import { FromTablet } from 'common/components/adaptive/tablet';
import { analyticsUtils } from 'utils/analytics-utils';
import { patchUrlByLocale } from 'utils';
import { memo } from 'common/hocs/memo';
import { getProductsData, getRatingsData } from '../../ducks/selectors';
import { ProductsCarouselItem } from './products-carousel-item';
import {
  bottomLineStyles,
  bottomSeeMoreStyles,
  carouselItemStyles,
  carouselStyles,
  carouselWrapperStyles,
  flatListStyles,
  flatListStylesWithoutHeight,
  helperRating,
  mobileItem,
  mobileItemPreview,
  pageContentStyles,
  productCardStyles,
  seeMoreCarouselItem,
  titleStyle,
  topLineStyles,
} from './styles';
import { ProductsCarouselControl } from './products-carousel-control';
import { ProductsCarouselComponentProps } from './types';
import { ChevronRightIcon } from '@fronton/icons-react';
import { Product as ProductType } from 'common/components/product-card/types';
import { useLoyaltyRewards } from 'common/hooks/use-loyalty-rewards';
import { useTranslation } from 'react-i18next';
import { Product } from '../product';
import { ProductsCarouselBottomLine } from './products-carousel-bottom-line';
import { SponsoredProductType } from 'features/products/types';
import { useSponsoredProducts } from './use-sponsored-products';
import { SeeMoreButton } from 'common/components/products-carousel/see-more-button';
import { SeeMoreButtonTop } from 'common/components/products-carousel/see-more-button-top';
import { topLineItemStyles } from 'common/components/products-carousel/styles';
import { CarouselSubtitle } from 'common/components/sponsored-carousel/carousel-subtitle/carousel-subtitle';

export const ProductsCarousel = memo<ProductsCarouselComponentProps>(
  ({
    isFromPdp = false,
    className,
    carouselClassName,
    innerContainerClassName,
    flatListClassName,
    titleClassName,
    title,
    titleAs = 'h2',
    subtitle,
    showSeeMore = true,
    seeMoreLink = '/',
    seeAllModal = false,
    slWidgetId,
    elementBlock = 'not set',
    'data-qa': dataQA,
    portalRootId,
    analytics,
    showLoyaltyRewards = false,
    sponsored,
  }) => {
    const pathname = useSelector(getPathname);
    const products: ProductType[] | SponsoredProductType[] | null = useSelector(getProductsData);
    const ratings = useSelector(getRatingsData);

    const { products: productsToDisplay, shouldShowSponsored } = useSponsoredProducts(products, sponsored);
    const showSponsored = shouldShowSponsored(sponsored);

    const titleActual = showSponsored ? sponsored.title : title;
    const carouselSubtitle = showSponsored ? sponsored?.subtitle : undefined;
    const showSeeMoreActual = showSponsored ? sponsored.showSeeMore : showSeeMore;
    const seeMoreLinkActual = patchUrlByLocale(
      showSponsored ? sponsored.seeMoreLink : seeMoreLink,
      pathname,
    );
    const seeMoreModalActual = showSponsored ? sponsored.seeAllModal : seeAllModal || isFromPdp;

    useLoyaltyRewards({
      enabled: showLoyaltyRewards,
      products: productsToDisplay,
    });

    const [canScrollRight, setCanScrollRight] = useState(false);

    const { t } = useTranslation();
    const seeAllButtonCaption = t('ui.button.see-all');

    const seeMoreLinkOnClickHandler = () => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementBlock,
        elementType: 'button',
        elementName: titleActual,
        elementText: seeAllButtonCaption,
        elementLink: seeMoreLinkActual,
      });
    };
    const topBarVisible = titleActual || showSeeMoreActual;
    const limitedProducts = useMemo(
      () => (productsToDisplay ? productsToDisplay.slice(0, 4) : null),
      [productsToDisplay],
    );

    const mobileFlatList = cn(
      limitedProducts && limitedProducts.length > 2 ? flatListStyles : flatListStylesWithoutHeight,
      flatListClassName,
    );

    if (!productsToDisplay?.length) return null;

    return (
      <Layout className={cn(pageContentStyles, className)}>
        <Rating className={helperRating} value={0} />
        <div className={carouselWrapperStyles} data-qa={dataQA}>
          {carouselSubtitle && <CarouselSubtitle subtitle={carouselSubtitle} />}

          {topBarVisible && (
            <div className={topLineStyles}>
              {titleActual && (
                <div className={topLineItemStyles}>
                  {titleActual && (
                    <Typography
                      className={cn(isFromPdp ? titleStyle : '', titleClassName)}
                      variant={titleAs}
                      data-qa="products-carousel-title"
                      color="text-primary"
                    >
                      {titleActual}
                    </Typography>
                  )}
                </div>
              )}

              {canScrollRight && showSeeMoreActual && (
                <SeeMoreButtonTop
                  {...{ seeAllButtonCaption, seeMoreLinkActual, seeMoreLinkOnClickHandler }}
                />
              )}
            </div>
          )}

          {/* Flat list for mobile version */}
          {limitedProducts && (
            <AtMobile>
              <div
                className={mobileFlatList}
                data-sl-widget-id={slWidgetId}
                data-sl-external="true"
                data-qa="products-carousel-flatlist"
              >
                {limitedProducts.map((product, index) => (
                  <Product
                    key={product.productId + index}
                    productPosition={index + 1}
                    className={cx(productCardStyles, mobileItem, mobileItemPreview)}
                    product={product}
                    subtitle={subtitle}
                    reviews={ratings}
                    elementBlock={elementBlock}
                    inWidget
                    analytics={analytics}
                  />
                ))}
              </div>
            </AtMobile>
          )}
          <FromTablet>
            <Carousel
              className={cn(carouselStyles, carouselClassName)}
              innerContainerClassName={cn(innerContainerClassName)}
              canScrollCallBack={(_, right) => setCanScrollRight(right ?? false)}
              controlLeft={<ProductsCarouselControl direction="left" />}
              controlRight={<ProductsCarouselControl direction="right" />}
              data-qa="products-carousel"
              slWidgetId={slWidgetId}
            >
              {productsToDisplay.map((product, index) => (
                <ProductsCarouselItem
                  key={product.productId + index}
                  className={carouselItemStyles}
                  index={index + 1}
                  product={product}
                  subtitle={subtitle}
                  ratings={ratings}
                  elementBlock={elementBlock}
                  data-sl-product-slot={index}
                  analytics={analytics}
                />
              ))}

              {/* "Смотреть всё" card with link at the end of the carousel list */}
              {showSeeMoreActual && !!productsToDisplay.length && (
                <div
                  className={cn(carouselItemStyles, seeMoreCarouselItem)}
                  data-qa="products-carousel-item-seemore"
                >
                  <SeeMoreButton
                    {...{ seeAllButtonCaption, seeMoreLinkActual, seeMoreLinkOnClickHandler }}
                  />
                </div>
              )}
            </Carousel>
          </FromTablet>

          {/* Bottom line */}
          {!seeMoreModalActual && showSeeMoreActual && (
            <AtMobile>
              <div className={bottomLineStyles}>
                <LinkButton
                  variant="normal"
                  size="m"
                  href={seeMoreLinkActual}
                  onClick={seeMoreLinkOnClickHandler}
                  withExtraClickZone
                  className={bottomSeeMoreStyles}
                  data-qa="products-carousel-seemore-link"
                >
                  <span>{seeAllButtonCaption}</span>
                  <ChevronRightIcon type="outline" size="l" color="text-minor" />
                </LinkButton>
              </div>
            </AtMobile>
          )}

          {/* Bottom line */}
          {seeMoreModalActual && (
            <ProductsCarouselBottomLine
              products={productsToDisplay}
              subtitle={subtitle}
              slWidgetId={slWidgetId}
              ratings={ratings}
              elementBlock={elementBlock}
              title={titleActual}
              portalRootId={portalRootId}
            />
          )}
        </div>
      </Layout>
    );
  },
);
